import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import type {
  SaveWidgetRequest,
  AddWidgetToDashboardRequest,
  ChartWidgetApiContextValue,
} from '@trinity/chat-components/containers/ChartWidget/Context.js';
import { getPagePlacementBlocks, insertSegWidget } from '@trinity/chat-components/containers/ChartWidget/placement.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useInvalidateChartLibraryWidgetsQuery } from '@trinity/chat-components/hooks/useInvalidateChartLibraryWidgetsQuery';
import { queryKeys } from '@trinity/chat-components/query-keys/index.js';

// Project-scoped adapter for `/projects/:projectId/segments*` (apps/chat-api's
// routers/v1/projects/segments.ts). Deliberately distinct from
// Project.QuantAnalysis/hooks/useChartWidgetApi.ts, which targets the unrelated
// flat `/segments` segments_v2 endpoints via `apiClient.segments`.
export const useChartWidgetApiContext = (): ChartWidgetApiContextValue => {
  const apiClient = useChatApiClient();

  return useMemo(
    () => ({
      useSaveWidget: () => {
        const invalidateChartLibraryWidgets = useInvalidateChartLibraryWidgetsQuery();
        return useMutation({
          mutationFn: (data: SaveWidgetRequest) => {
            return apiClient.projects.createProjectWidget({
              projectId: data.projectId,
              title: data.title,
              data: {
                type: data.data.type,
                props: data.data.props,
              },
              sourceMetadata: data.sourceMetadata,
            });
          },
          onSuccess: (_data, variables) => {
            invalidateChartLibraryWidgets(variables.projectId);
          },
          onError: () => {
            Toast.error({
              title: 'Save Failed',
              body: 'There was an error saving the chart widget.',
            });
          },
        });
      },

      useGetSegments: (projectId: number, enabled = true) =>
        useQuery({
          queryKey: ['project-segments', projectId],
          queryFn: async () => {
            const response = await apiClient.projects.fetchProjectSegments({ projectId });
            return { segments: response.items.map(({ segment }) => segment) };
          },
          enabled: enabled && !!projectId,
        }),

      useGetPlacementBlocks: (projectId: number, segmentId: number | null) =>
        useQuery({
          // Same key as the dashboard screen (useSegmentPageState), so placements show there at once.
          queryKey: queryKeys.projects.segmentPage({ projectId, segmentId: segmentId ?? 0 }),
          queryFn: () => apiClient.projects.getProjectSegmentPage({ projectId, segmentId: segmentId ?? 0 }),
          select: getPagePlacementBlocks,
          enabled: !!projectId && segmentId != null,
        }),

      useAddWidgetToDashboard: (projectId: number) => {
        const queryClient = useQueryClient();
        return useMutation({
          mutationFn: async ({ segmentId, ...target }: AddWidgetToDashboardRequest) => {
            const current = await apiClient.projects.getProjectSegmentPage({ projectId, segmentId });
            const updated = insertSegWidget(current, target);
            await apiClient.projects.updateProjectSegmentPage({ projectId, segmentId, config: updated });
            return updated;
          },
          onError: () => {
            Toast.error({
              title: 'Placement Failed',
              body: 'There was an error adding the widget to the dashboard.',
            });
          },
          onSuccess: (updated, { segmentId }) => {
            queryClient.setQueryData(queryKeys.projects.segmentPage({ projectId, segmentId }), updated);
            queryClient.invalidateQueries({ queryKey: queryKeys.projects.segmentPageVersions({ projectId, segmentId }) }).catch(() => undefined);
            // The dashboard prunes widgets missing from its library query — refresh it so a
            // just-saved chart isn't hidden.
            queryClient.invalidateQueries({ queryKey: ['get:projects/widgets', projectId] }).catch(() => undefined);
            Toast.success({
              title: 'Widget added successfully',
              body: 'The widget has been added to the selected dashboard.',
            });
          },
        });
      },
    }),
    [apiClient]
  );
};
